/**
 * Header Navigation — Frontend behavior
 * Handles: sticky scroll, desktop dropdown hover/keyboard, mobile bottom-sheet drawer
 */

document.addEventListener( 'DOMContentLoaded', () => {
	const header     = document.getElementById( 'site-header' );
	const mainNav    = document.getElementById( 'gnk-main-nav' );
	const mobOpen    = document.getElementById( 'gnk-mob-open' );
	const mobOverlay = document.getElementById( 'gnk-mob-overlay' );
	const mobDrawer  = document.getElementById( 'gnk-mob-drawer' );
	const drawerList = document.getElementById( 'gnk-drawer-list' );

	if ( ! header || ! mainNav ) return;

	// Strip hidden from all dropdown panels so CSS visibility/opacity can transition
	mainNav.querySelectorAll( '.gnk-dropdown' ).forEach( ( p ) => p.removeAttribute( 'hidden' ) );

	/* ── Sticky scroll ── */
	const isSticky = header.dataset.sticky === 'true';
	if ( isSticky ) {
		const sentinel = document.createElement( 'div' );
		sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
		document.body.insertBefore( sentinel, document.body.firstChild );

		const observer = new IntersectionObserver(
			( [ entry ] ) => {
				header.classList.toggle( 'is-sticky', ! entry.isIntersecting );
			},
			{ rootMargin: '0px 0px 0px 0px', threshold: 0 }
		);
		observer.observe( sentinel );
	}

	/* ── Desktop dropdowns ── */
	const isMobile = () => window.innerWidth <= 768;
	let hoverTimer = null;

	function closeAllDropdowns( except ) {
		mainNav.querySelectorAll( '.gnk-nav-item.is-open' ).forEach( ( item ) => {
			if ( item !== except ) {
				item.classList.remove( 'is-open' );
				const btn = item.querySelector( '.gnk-nav-link' );
				if ( btn ) btn.setAttribute( 'aria-expanded', 'false' );
			}
		} );
	}

	function openDropdown( item ) {
		item.classList.add( 'is-open' );
		const btn = item.querySelector( '.gnk-nav-link' );
		if ( btn ) btn.setAttribute( 'aria-expanded', 'true' );
	}

	function closeDropdown( item ) {
		item.classList.remove( 'is-open' );
		const btn = item.querySelector( '.gnk-nav-link' );
		if ( btn ) btn.setAttribute( 'aria-expanded', 'false' );
	}

	mainNav.querySelectorAll( '.gnk-nav-item' ).forEach( ( item ) => {
		const panel = item.querySelector( '.gnk-dropdown' );
		if ( ! panel ) return;

		const btn = item.querySelector( '.gnk-nav-link' );

		// Hover intent (desktop only)
		item.addEventListener( 'mouseenter', () => {
			if ( isMobile() ) return;
			clearTimeout( hoverTimer );
			hoverTimer = setTimeout( () => {
				closeAllDropdowns( item );
				openDropdown( item );
			}, 150 );
		} );

		item.addEventListener( 'mouseleave', () => {
			if ( isMobile() ) return;
			clearTimeout( hoverTimer );
			hoverTimer = setTimeout( () => closeDropdown( item ), 200 );
		} );

		// Click toggle
		if ( btn ) {
			btn.addEventListener( 'click', ( e ) => {
				if ( isMobile() ) return;
				e.preventDefault();
				const wasOpen = item.classList.contains( 'is-open' );
				closeAllDropdowns();
				if ( ! wasOpen ) openDropdown( item );
			} );

			btn.addEventListener( 'keydown', ( e ) => {
				if ( e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					const wasOpen = item.classList.contains( 'is-open' );
					closeAllDropdowns();
					if ( ! wasOpen ) openDropdown( item );
				}
			} );
		}

		// Tab/Shift+Tab focus containment within the open panel
		panel.addEventListener( 'keydown', ( e ) => {
			if ( e.key !== 'Tab' || ! item.classList.contains( 'is-open' ) ) return;
			const focusable = Array.from(
				panel.querySelectorAll( 'a[href], button:not([disabled])' )
			);
			if ( ! focusable.length ) return;
			const first = focusable[ 0 ];
			const last  = focusable[ focusable.length - 1 ];
			if ( e.shiftKey && document.activeElement === first ) {
				e.preventDefault();
				closeDropdown( item );
				if ( btn ) btn.focus();
			} else if ( ! e.shiftKey && document.activeElement === last ) {
				e.preventDefault();
				closeDropdown( item );
			}
		} );
	} );

	// Escape closes any open dropdown or drawer
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) {
			const open = mainNav.querySelector( '.gnk-nav-item.is-open' );
			if ( open ) {
				closeDropdown( open );
				const focusable = open.querySelector( '.gnk-nav-link' );
				if ( focusable ) focusable.focus();
			}
			if ( mobDrawer && ! mobDrawer.hasAttribute( 'hidden' ) ) {
				closeMobileDrawer();
			}
		}
	} );

	// Click outside closes dropdowns
	document.addEventListener( 'click', ( e ) => {
		if ( ! mainNav.contains( e.target ) ) {
			closeAllDropdowns();
		}
	} );

	/* ── Search expand ── */
	const searchToggle  = document.getElementById( 'gnk-search-toggle' );
	const searchForm    = document.getElementById( 'gnk-search-form' );
	const searchSvgOpen = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="6.5" cy="6.5" r="4.5" stroke="white" stroke-width="1.5"/><path d="M10 10l3.5 3.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>';
	const searchSvgClose = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>';

	if ( searchToggle && searchForm ) {
		searchToggle.addEventListener( 'click', () => {
			const isOpen = searchToggle.getAttribute( 'aria-expanded' ) === 'true';
			searchToggle.setAttribute( 'aria-expanded', isOpen ? 'false' : 'true' );
			searchToggle.innerHTML = isOpen ? searchSvgOpen : searchSvgClose;
			searchForm.classList.toggle( 'is-open', ! isOpen );
			searchForm.setAttribute( 'aria-hidden', isOpen ? 'true' : 'false' );
			if ( ! isOpen ) {
				const input = searchForm.querySelector( 'input[type="search"]' );
				if ( input ) setTimeout( () => input.focus(), 50 );
			}
		} );

		document.addEventListener( 'click', ( e ) => {
			if ( ! e.target.closest( '.gnk-search-wrap' ) ) {
				searchToggle.setAttribute( 'aria-expanded', 'false' );
				searchToggle.innerHTML = searchSvgOpen;
				searchForm.classList.remove( 'is-open' );
				searchForm.setAttribute( 'aria-hidden', 'true' );
			}
		} );
	}

	/* ── Mobile bottom-sheet drawer ── */
	if ( ! mobOpen || ! mobDrawer || ! mobOverlay ) return;

	function escHtml( str ) {
		const div = document.createElement( 'div' );
		div.appendChild( document.createTextNode( str ) );
		return div.innerHTML;
	}

	function buildDrawerContent() {
		if ( ! drawerList ) return;
		drawerList.innerHTML = '';

		mainNav.querySelectorAll( '.gnk-nav-list > .gnk-nav-item' ).forEach( ( item ) => {
			const link     = item.querySelector( '.gnk-nav-link' );
			const label    = link ? link.textContent.trim() : '';
			const href     = link ? ( link.getAttribute( 'href' ) || '#' ) : '#';
			const hasKids  = item.classList.contains( 'has-dropdown' );
			const isActive = item.classList.contains( 'current-menu-item' );

			const li = document.createElement( 'li' );
			li.className = 'gnk-drawer-item';

			if ( hasKids ) {
				const btn = document.createElement( 'button' );
				btn.className = 'gnk-drawer-link' + ( isActive ? ' active' : '' );
				btn.type = 'button';
				btn.setAttribute( 'aria-expanded', 'false' );
				btn.innerHTML = `<span>${ escHtml( label ) }</span>
					<svg class="gnk-drawer-chev-svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

				const childUl = document.createElement( 'ul' );
				childUl.className = 'gnk-drawer-children';
				childUl.setAttribute( 'hidden', '' );

				item.querySelectorAll( '.gnk-dropdown .gnk-drop-item' ).forEach( ( dropItem ) => {
					const childLabel = dropItem.querySelector( '.gnk-drop-label' );
					const childText  = childLabel ? childLabel.textContent.trim() : dropItem.textContent.trim();
					const childHref  = dropItem.getAttribute( 'href' ) || '#';
					const childLi    = document.createElement( 'li' );
					const childA     = document.createElement( 'a' );
					childA.className   = 'gnk-drawer-child';
					childA.href        = childHref;
					childA.textContent = childText;
					childLi.appendChild( childA );
					childUl.appendChild( childLi );
				} );

				btn.addEventListener( 'click', () => {
					const isOpen = btn.getAttribute( 'aria-expanded' ) === 'true';
					btn.setAttribute( 'aria-expanded', isOpen ? 'false' : 'true' );
					if ( isOpen ) {
						childUl.setAttribute( 'hidden', '' );
					} else {
						childUl.removeAttribute( 'hidden' );
					}
				} );

				li.appendChild( btn );
				li.appendChild( childUl );
			} else {
				const a = document.createElement( 'a' );
				a.className   = 'gnk-drawer-link' + ( isActive ? ' active' : '' );
				a.href        = href;
				a.textContent = label;
				li.appendChild( a );
			}

			drawerList.appendChild( li );
		} );
	}

	function openMobileDrawer() {
		buildDrawerContent();
		mobDrawer.removeAttribute( 'hidden' );
		mobOverlay.removeAttribute( 'aria-hidden' );
		mobOverlay.classList.add( 'is-open' );
		requestAnimationFrame( () => {
			mobDrawer.classList.add( 'is-open' );
		} );
		mobOpen.setAttribute( 'aria-expanded', 'true' );
		document.body.style.overflow = 'hidden';
	}

	function closeMobileDrawer() {
		mobDrawer.classList.remove( 'is-open' );
		mobOverlay.classList.remove( 'is-open' );
		mobOverlay.setAttribute( 'aria-hidden', 'true' );
		mobOpen.setAttribute( 'aria-expanded', 'false' );
		document.body.style.overflow = '';
		setTimeout( () => {
			mobDrawer.setAttribute( 'hidden', '' );
		}, 320 );
	}

	mobOpen.addEventListener( 'click', openMobileDrawer );
	mobOverlay.addEventListener( 'click', closeMobileDrawer );
} );
